import type {
  FieldKind,
  FieldSpecOf
} from 'v5/core/entity-schema/entitySchema.types';
import type { SelectOption } from 'v5/core/form-schema/formSchema.types';
import moment from 'moment-timezone';
import { heightInches } from 'v5/core/entity-values/codecs/height.codec';
import { formatMoneyAmount } from 'v5/core/money/formatMoney';
import type {
  FormValueOf,
  LegacyMeasurementValue,
  OptionRef
} from 'v5/core/entity-values/values.types';

type ValueText<K extends FieldKind> = (
  value: FormValueOf<K>,
  spec: FieldSpecOf<K>
) => string | null;

/** Each kind's value as plain text (`FieldKit.text`); a kind missing here does not compile. */
export const resourceValueText: { [K in FieldKind]: ValueText<K> } = {
  text: value => nonEmpty(value),
  // "Yes" / "No" find nothing anyone types.
  boolean: () => null,
  number: value => (value === null ? null : String(value)),
  date: value => (value === null ? null : formatDate(value)),
  'date-range': value =>
    joined(
      [value.from, value.to].map(date =>
        date === null ? null : formatDate(date)
      ),
      ' – '
    ),
  'single-select': (value, spec) => optionLabel(spec.options, value),
  'multi-select': (value, spec) =>
    joined(value.map(id => optionLabel(spec.options, id))),
  measurement: value => {
    switch (value.unit) {
      case 'ft':
        return nonEmpty(
          heightInches.format(
            heightInches.join(
              feetOrInches(value.feet),
              feetOrInches(value.inches)
            )
          )
        );
      case 'cm':
      case 'm':
      case 'in':
        return value.amount === '' ? null : `${value.amount} ${value.unit}`;
    }
  },
  currency: value => money(value.currency, value.amount),
  url: value => nonEmpty(value),
  percentage: value => (value === null ? null : `${value}%`),
  height: value => nonEmpty(heightInches.format(value)),
  dimension: (value, spec) =>
    joined(
      [value.x, value.y, value.z].map(axis =>
        axis === null ? null : amountText(axis, spec.units)
      ),
      ' × '
    ),
  'measurement-legacy': (value, spec) => amountText(value, spec.units),
  // Project names come from the integration.
  'netsuite-master-project': () => null,
  name: value => joined([value.first_name, value.last_name], ' '),
  pronouns: (value, spec) =>
    spec.options.find(option => String(option.id) === value)?.value ?? null,
  'contact-details': value => joined(value.map(item => item.value)),
  tags: (value, spec) => optionRefsText(value, spec.options),
  groups: (value, spec) => optionRefsText(value, spec.options),
  location: value => nonEmpty(value.name),
  rate: (value, spec) => {
    const card =
      value.rate_id === null
        ? undefined
        : spec.rateCards.find(rateCard => rateCard.id === value.rate_id);
    if (card) return card.name;
    return joined([value.name, money(value.currency, value.amount)], ' · ');
  },
  // The kit names it from its source (`EntityRefSource.nameOf`).
  'entity-ref': () => null
};

function nonEmpty(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

/** The non-empty parts joined; `null` when none is left. */
function joined(
  parts: ReadonlyArray<string | null>,
  separator = ', '
): string | null {
  const texts = parts.flatMap(part => {
    const text = part === null ? null : nonEmpty(part);
    return text === null ? [] : [text];
  });
  return texts.length === 0 ? null : texts.join(separator);
}

function optionLabel(
  options: ReadonlyArray<SelectOption>,
  value: string | null
): string | null {
  return options.find(option => option.value === value)?.label ?? null;
}

function optionRefsText(
  refs: ReadonlyArray<OptionRef>,
  options: ReadonlyArray<SelectOption>
): string | null {
  return joined(
    refs.map(ref =>
      ref.type === 'new' ? ref.label : optionLabel(options, String(ref.id))
    )
  );
}

function amountText(
  value: LegacyMeasurementValue,
  units: ReadonlyArray<SelectOption>
): string | null {
  if (value.amount === null) return null;
  const unit = optionLabel(units, value.unit) ?? value.unit;
  return `${value.amount} ${unit}`.trim();
}

/** As the tables show a date custom field. */
function formatDate(date: Date): string {
  return moment(date).format('Do MMM YYYY');
}

/** As the form shows an amount at rest (`CurrencyField`). */
function money(currency: string, amount: number | null): string | null {
  return amount === null
    ? null
    : `${currency} ${formatMoneyAmount(amount, 'en')}`;
}

function feetOrInches(text: string): number | null {
  return text.trim() === '' ? null : Number(text);
}
